'use client';

import { motion, type Variants } from 'framer-motion';
import {
  CalendarCheck,
  Clock,
  Target,
  MessageCircle,
  TrendingUp,
  FileText,
  type LucideIcon,
} from 'lucide-react';

interface Feature {
  icon: LucideIcon;
  title: string;
  desc: string;
  iconBg: string;
  textAccent: string;
}

const features: Feature[] = [
  {
    icon: CalendarCheck,
    title: '오늘의 체크인',
    desc: '아침엔 오늘의 방향을, 저녁엔 하루의 의미를. 1분이면 충분하고, AI가 패턴을 읽어요.',
    iconBg: '#10b981',
    textAccent: '#059669',
  },
  {
    icon: Clock,
    title: '집중 타이머',
    desc: '뽀모도로로 몰입하고, 세션이 끝나면 AI가 오늘의 집중을 돌아보는 질문을 드려요.',
    iconBg: '#6366f1',
    textAccent: '#4f46e5',
  },
  {
    icon: Target,
    title: '목표 관리',
    desc: '큰 꿈을 말하면, AI가 오늘 당장 할 수 있는 작은 행동으로 나눠드려요.',
    iconBg: '#f97316',
    textAccent: '#ea580c',
  },
  {
    icon: MessageCircle,
    title: 'AI 코치',
    desc: '기록이 쌓일수록 코칭이 정교해집니다. 나만의 고민을 편하게 물어보세요.',
    iconBg: '#8b5cf6',
    textAccent: '#7c3aed',
  },
  {
    icon: TrendingUp,
    title: '성장 추적',
    desc: '매일의 기록이 나만의 성장 곡선이 됩니다. 강점과 성장 기회가 한눈에 보여요.',
    iconBg: '#ec4899',
    textAccent: '#db2777',
  },
  {
    icon: FileText,
    title: '주간 리포트',
    desc: '한 주를 AI가 정리해주고, 다음 주에 집중할 포인트를 콕 짚어드려요.',
    iconBg: '#f59e0b',
    textAccent: '#d97706',
  },
];

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0 },
};

export default function FeatureShowcase() {
  return (
    <section className="py-28 sm:py-36" style={{ backgroundColor: '#fafafa' }}>
      <div className="container mx-auto px-4 max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          className="text-center mb-20"
        >
          {/* Coach speech bubble */}
          <div className="inline-flex items-start gap-3 mb-8">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #6366f1, #4338ca)' }}
            >
              AI
            </div>
            <div
              className="rounded-2xl rounded-tl-sm px-5 py-3 text-left text-sm leading-relaxed text-gray-700"
              style={{ backgroundColor: '#f3f0ff', maxWidth: '320px' }}
            >
              매일 쓸 수 있는 도구 6가지를 준비했어요.
              <br />
              어떤 게 있는지 한번 볼까요?
            </div>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 mb-5">
            하루를 바꾸는 6가지 습관
          </h2>
          <p className="text-gray-500 max-w-md mx-auto text-base sm:text-lg leading-relaxed">
            기록하고, 집중하고, 돌아보세요. AI 코치가 매일 함께합니다.
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-60px' }}
          transition={{ staggerChildren: 0.08 }}
          className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
        >
          {features.map((f) => (
            <motion.div
              key={f.title}
              variants={cardVariants}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              whileHover={{ y: -6, transition: { duration: 0.2 } }}
              className="rounded-3xl p-5 sm:p-6 cursor-default overflow-hidden relative bg-white transition-shadow hover:shadow-xl"
              style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}
            >
              <div className="relative">
                {/* Icon circle */}
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: f.iconBg }}
                >
                  <f.icon className="w-6 h-6 text-white" />
                </div>

                {/* Title */}
                <h3 className="text-base sm:text-lg font-extrabold text-gray-900 mb-1.5">{f.title}</h3>

                {/* Description */}
                <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
